import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import type { MaterialLot } from "@/types/legacy";

/**
 * Hook para obtener los lotes activos de un material
 */
export function useLotsForMaterial(materialId: string | undefined) {
  return useQuery({
    queryKey: ["material-lots", materialId],
    queryFn: async () => {
      if (!materialId) return [];

      const { data, error } = await api
        .from("material_lots")
        .select("*")
        .eq("material_id", materialId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as MaterialLot[];
    },
    enabled: !!materialId,
  });
}

interface RegisterLotMovementParams {
  materialId: string;
  lotId: string;
  movementType: "in" | "out" | "adjustment";
  quantity: number;
  squareMetersChange?: number;
  reason: string;
  notes?: string;
  currentLotStock: number;
  currentLotSquareMeters?: number;
}

/**
 * Hook para registrar un movimiento en un lote específico
 * Actualiza el lote, recalcula el material padre, y registra el movimiento
 */
export function useRegisterLotMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RegisterLotMovementParams) => {
      const {
        materialId,
        lotId,
        movementType,
        quantity,
        squareMetersChange,
        reason,
        notes,
        currentLotStock,
        currentLotSquareMeters,
      } = params;

      const user = getCurrentUser();

      // Calculate new lot values
      let newLotStock: number;
      let movementQuantity: number;

      if (movementType === "in") {
        newLotStock = currentLotStock + quantity;
        movementQuantity = quantity;
      } else if (movementType === "out") {
        newLotStock = currentLotStock - quantity;
        movementQuantity = -quantity;
      } else {
        // adjustment
        newLotStock = quantity;
        movementQuantity = quantity - currentLotStock;
      }

      // Calculate square meters if applicable
      let newLotSquareMeters = currentLotSquareMeters;
      if (squareMetersChange !== undefined && currentLotSquareMeters !== undefined) {
        if (movementType === "in") {
          newLotSquareMeters = currentLotSquareMeters + squareMetersChange;
        } else if (movementType === "out") {
          newLotSquareMeters = currentLotSquareMeters - squareMetersChange;
        } else {
          newLotSquareMeters = squareMetersChange;
        }
      }

      // 1. Update the lot
      const lotUpdateData: Record<string, any> = {
        quantity: newLotStock,
        updated_at: new Date().toISOString(),
      };
      if (newLotSquareMeters !== undefined) {
        lotUpdateData.square_meters = newLotSquareMeters;
      }

      const { error: lotError } = await api
        .from("material_lots")
        .update(lotUpdateData)
        .eq("id", lotId);

      if (lotError) throw lotError;

      // 2. Recalculate parent material totals from all active lots
      const { data: allLots, error: lotsError } = await api
        .from("material_lots")
        .select("quantity, square_meters")
        .eq("material_id", materialId)
        .eq("is_active", true);

      if (lotsError) throw lotsError;

      const totalQuantity = (allLots || []).reduce(
        (sum, lot) => sum + (lot.quantity || 0),
        0
      );
      const totalSquareMeters = (allLots || []).reduce(
        (sum, lot) => sum + (lot.square_meters || 0),
        0
      );

      // Get current material stock for movement record
      const { data: materialData, error: matFetchError } = await api
        .from("material_inventory")
        .select("current_stock")
        .eq("id", materialId)
        .single();

      if (matFetchError) throw matFetchError;

      const previousStock = materialData?.current_stock || 0;

      // 3. Update material_inventory totals
      const { error: materialError } = await api
        .from("material_inventory")
        .update({
          current_stock: totalQuantity,
          square_meters: totalSquareMeters,
          last_restock_date:
            movementType === "in"
              ? new Date().toISOString().split("T")[0]
              : undefined,
          updated_at: new Date().toISOString(),
        })
        .eq("id", materialId);

      if (materialError) throw materialError;

      // 4. Register the movement with lot_id
      const { error: movementError } = await api
        .from("inventory_movements")
        .insert({
          material_inventory_id: materialId,
          lot_id: lotId,
          movement_type: movementType === "adjustment" ? "adjustment" : movementType,
          quantity: movementQuantity,
          reason,
          notes: notes || null,
          performed_by: user?.id,
          previous_stock: previousStock,
          new_stock: totalQuantity,
        });

      if (movementError) throw movementError;

      return { success: true, newLotStock, totalQuantity };
    },
    onSuccess: (_, variables) => {
      const message =
        variables.movementType === "in"
          ? "Entrada registrada correctamente"
          : variables.movementType === "out"
          ? "Salida registrada correctamente"
          : "Ajuste realizado correctamente";

      toast.success(message);

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ["material-lots", variables.materialId],
      });
      queryClient.invalidateQueries({
        queryKey: ["material-inventory", variables.materialId],
      });
      queryClient.invalidateQueries({
        queryKey: ["inventory-movements", variables.materialId],
      });
      queryClient.invalidateQueries({ queryKey: ["material-inventory"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al registrar movimiento");
    },
  });
}
